import type { IAppDetail, IEntryDetail } from '@baishuyun/types';
import type { EntryTreeItem, BsEntrySelectChangeResult } from './types';

export const DEPTH_PADDING: Record<number, string> = {
  0: 'pl-[10px]',
  1: 'pl-[32px]',
  2: 'pl-[32px]',
  3: 'pl-[54px]',
  4: 'pl-[78px]',
  5: 'pl-[102px]',
};

export function getDepthPadding(depth: number): string {
  return DEPTH_PADDING[Math.min(depth, 5)] ?? DEPTH_PADDING[5];
}

export function getEntryIconClass(entry: IEntryDetail): string {
  const isNew = entry.icon >= 25;
  const newStr = isNew ? '-new' : '';
  return `node-icon icon-bsy-form${newStr}  icon${newStr}-bsy-form-${entry.icon}`;
}

export function buildEntryTreeFromAliasMap(
  appDetail: IAppDetail,
  searchQuery: string
): EntryTreeItem[] {
  const { entryIdAliasMap, entryMap } = appDetail;
  const aliasKeys = Object.keys(entryIdAliasMap);
  if (aliasKeys.length === 0) return [];

  const childrenByParent = new Map<string, string[]>();
  for (const id of aliasKeys) {
    const aliasItem = entryIdAliasMap[id];
    const parentKey = aliasItem.parent || '0';
    if (!childrenByParent.has(parentKey)) {
      childrenByParent.set(parentKey, []);
    }
    childrenByParent.get(parentKey)!.push(id);
  }

  // Root parents: any parent value that is not itself a key in entryIdAliasMap.
  // Also include the sentinel '0' for entries with empty/falsy parent.
  const allParentValues = new Set(childrenByParent.keys());
  const rootParents = Array.from(allParentValues).filter((p) => p === '0' || !entryIdAliasMap[p]);

  const lowerQuery = searchQuery.toLowerCase().trim();

  function buildRecursive(parentId: string, depth: number): EntryTreeItem[] {
    const childIds = childrenByParent.get(parentId) || [];
    const nodes: EntryTreeItem[] = [];

    for (const id of childIds) {
      const aliasItem = entryIdAliasMap[id];
      const entryDetail = entryMap[id] || null;
      const name = entryDetail?.name || id;
      const icon = entryDetail?.icon || 0;
      const isGroup = aliasItem.type === 'group';

      const children = buildRecursive(id, depth + 1);
      const selfMatch = !lowerQuery || name?.toLowerCase().includes(lowerQuery);

      if (isGroup) {
        if (selfMatch || children.length > 0) {
          nodes.push({
            id,
            name,
            icon,
            type: 'group',
            children,
            depth,
            isSelectable: false,
            entry: entryDetail,
          });
        }
      } else {
        if (selfMatch) {
          nodes.push({
            id,
            name,
            icon,
            type: aliasItem.type,
            children: [],
            depth,
            isSelectable: true,
            entry: entryDetail,
          });
        }
      }
    }

    return nodes;
  }

  // Build from each root parent, all at depth 0
  const result: EntryTreeItem[] = [];
  for (const rootId of rootParents) {
    result.push(...buildRecursive(rootId, 0));
  }
  return result;
}

export function filterTreeBySearch(nodes: EntryTreeItem[], searchQuery: string): EntryTreeItem[] {
  const lowerQuery = searchQuery.toLowerCase().trim();
  if (!lowerQuery) return nodes;

  const result: EntryTreeItem[] = [];
  for (const node of nodes) {
    const nameMatch = node?.name.toLowerCase().includes(lowerQuery);
    const filteredChildren = filterTreeBySearch(node.children, searchQuery);

    if (!node.isSelectable) {
      if (nameMatch || filteredChildren.length > 0) {
        result.push({ ...node, children: filteredChildren });
      }
    } else {
      if (nameMatch) {
        result.push(node);
      }
    }
  }
  return result;
}

export function findSelected(
  appDetails: Record<string, IAppDetail>,
  entryId: string,
  currentAppId?: string
): BsEntrySelectChangeResult | null {
  for (const [appId, detail] of Object.entries(appDetails)) {
    if (detail.entryMap[entryId]) {
      return {
        entry: detail.entryMap[entryId],
        appId,
        section: appId === currentAppId ? 'current' : 'other',
      };
    }
  }
  return null;
}
